import { IconButton } from '@ui/buttons/icon-button';
import { NotificationsIcon } from '@ui/icons/material/Notifications';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { Badge } from '@ui/badge/badge';
import { Trans } from '@ui/i18n/trans';
import { CustomNotificationList } from '@app/notifications/custom-notification-list';
import { useActiveNotifications } from '@app/admin/notifications/requests/use-active-notifications';
import { NotificationEmptyStateMessage } from '../empty-state/notification-empty-state-message';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { useState, useEffect, useCallback } from 'react';

const VIEWED_NOTIFICATIONS_KEY = 'viewed_custom_notifications';

function getViewedNotificationIds(): number[] {
  try {
    const stored = localStorage.getItem(VIEWED_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function setViewedNotificationIds(ids: number[]): void {
  try {
    localStorage.setItem(VIEWED_NOTIFICATIONS_KEY, JSON.stringify(ids));
  } catch {
    // Ignore localStorage errors
  }
}

interface NotificationDialogTriggerProps {
  className?: string;
}

export function NotificationDialogTrigger({
  className,
}: NotificationDialogTriggerProps) {
  const { data: customData } = useActiveNotifications();
  const [viewedIds, setViewedIds] = useState<number[]>(() => getViewedNotificationIds());

  // Calculate unread count (custom notifications not yet viewed)
  const allNotificationIds = customData?.notifications?.map(n => n.id) || [];
  const unreadCount = allNotificationIds.filter(id => !viewedIds.includes(id)).length;
  const hasUnread = unreadCount > 0;

  // Mark all as viewed when popup opens
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen && allNotificationIds.length > 0) {
      // Merge current viewed with all notification ids
      const newViewedIds = [...new Set([...viewedIds, ...allNotificationIds])];
      setViewedIds(newViewedIds);
      setViewedNotificationIds(newViewedIds);
    }
  }, [allNotificationIds, viewedIds]);

  // Clean up old viewed IDs that no longer exist
  useEffect(() => {
    if (customData?.notifications) {
      const currentIds = customData.notifications.map(n => n.id);
      const cleanedViewedIds = viewedIds.filter(id => currentIds.includes(id));
      if (cleanedViewedIds.length !== viewedIds.length) {
        setViewedIds(cleanedViewedIds);
        setViewedNotificationIds(cleanedViewedIds);
      }
    }
  }, [customData?.notifications]);

  return (
    <DialogTrigger type="popover" onOpenChange={handleOpenChange}>
      <IconButton
        size="md"
        className={className}
        badge={
          hasUnread ? (
            <Badge className="max-md:hidden" color="bg-primary text-white">
              {unreadCount}
            </Badge>
          ) : undefined
        }
      >
        <NotificationsIcon />
      </IconButton>
      <NotificationsDialog />
    </DialogTrigger>
  );
}

export function NotificationsDialog() {
  return (
    <Dialog>
      <DialogHeader showDivider>
        <Trans message="Notifications" />
      </DialogHeader>
      <DialogBody padding="p-0">
        <DialogContent />
      </DialogBody>
    </Dialog>
  );
}

function DialogContent() {
  const { data: customData, isLoading } = useActiveNotifications();
  const hasCustomNotifications = !!customData?.notifications?.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-24 py-20">
        <ProgressCircle aria-label="Loading notifications..." isIndeterminate />
      </div>
    );
  }

  if (!hasCustomNotifications) {
    return (
      <div className="px-24 py-20">
        <NotificationEmptyStateMessage />
      </div>
    );
  }

  return (
    <div>
      <CustomNotificationList />
    </div>
  );
}
